// InAppDocViewer/PdfFirstPagePreview.jsx
//
// Renders a PDF's FIRST PAGE as the segment's inline preview (canvas) instead of
// the static thumbnail icon. This is opt-in per document (the caller only mounts
// it when the user toggles preview on AND the flag is ON). pdfjs-dist is loaded
// lazily here too, so nothing heavy is fetched unless a preview is actually shown.
//
// Behaviour-preserving fallback: while loading, and on ANY error or oversize PDF,
// it renders `fallback` (the existing <img> thumbnail the caller passes in). So a
// preview that fails never breaks the segment — it just looks like today.

import React, { useEffect, useRef, useState } from "react";
import { getPdfDocument, renderPageToCanvas } from "./pdfjs";
import { INAPP_DOC_PREVIEW_MAX_BYTES } from "./flag";

/**
 * @param {string} url        PDF url
 * @param {number} width      target render width in px (height keeps aspect)
 * @param {React.ReactNode} fallback  thumbnail to show while loading / on error
 * @param {object} style      style applied to the rendered canvas
 */
export default function PdfFirstPagePreview({
  url,
  width = 30,
  fallback = null,
  style = {},
}) {
  const canvasRef = useRef(null);
  const [phase, setPhase] = useState("loading"); // loading | ready | error

  useEffect(() => {
    let cancelled = false;
    setPhase("loading");

    const run = async () => {
      try {
        // Large-PDF guard: HEAD the file and skip inline render above the cap.
        // A HEAD failure is non-fatal — we just proceed and let the render try.
        try {
          const head = await fetch(url, { method: "HEAD" });
          const len = Number(head.headers.get("content-length") || 0);
          if (len && len > INAPP_DOC_PREVIEW_MAX_BYTES) {
            if (!cancelled) setPhase("error");
            return;
          }
        } catch (e) {
          /* HEAD not allowed / CORS — continue and attempt render */
        }

        const pdf = await getPdfDocument(url);
        if (cancelled) return;
        const page = await pdf.getPage(1);
        // Compute scale so the first page renders at the requested width.
        const baseViewport = page.getViewport({ scale: 1 });
        const scale = width / baseViewport.width;
        if (cancelled || !canvasRef.current) return;
        await renderPageToCanvas(pdf, 1, canvasRef.current, scale);
        if (!cancelled) setPhase("ready");
        try {
          if (pdf.destroy) pdf.destroy();
        } catch (e) {
          /* noop */
        }
      } catch (e) {
        if (!cancelled) setPhase("error");
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [url, width]);

  // On loading or error, show the caller's existing thumbnail (byte-identical
  // to today's look). Only show the canvas once a page has rendered.
  return (
    <>
      {phase !== "ready" && fallback}
      <canvas
        ref={canvasRef}
        aria-label="Document first-page preview"
        style={{
          display: phase === "ready" ? "block" : "none",
          borderRadius: "8px",
          ...style,
        }}
      />
    </>
  );
}
